import jsPDF from 'jspdf';
import 'jspdf-autotable';

// CSV Export Functions
export const exportToCSV = (data, filename) => {
  if (!data || data?.length === 0) {
    alert('No data to export');
    return;
  }

  const headers = Object.keys(data?.[0]);
  const csvContent = [
    headers?.join(','),
    ...data?.map(row => 
      headers?.map(header => {
        const value = row?.[header];
        // Escape commas and quotes in values
        if (typeof value === 'string' && (value?.includes(',') || value?.includes('"'))) {
          return `"${value?.replace(/"/g, '""')}"`;
        }
        return value;
      })?.join(',')
    )
  ]?.join('\n');

  downloadFile(csvContent, filename, 'text/csv');
};

// PDF Export Functions
export const exportCalendarToPDF = (activities, persons, currentDate) => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text('Family Calendar Export', 14, 20);
  
  // Date range info
  doc?.setFontSize(11);
  doc?.text(`Export Date: ${new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Prepare table data
  const tableData = activities?.map(activity => [
    activity?.date || '',
    activity?.time || '',
    activity?.title || '',
    activity?.personName || '',
    activity?.location || '',
    activity?.category || ''
  ]) || [];
  
  // Generate table
  doc?.autoTable({
    head: [['Date', 'Time', 'Activity', 'Person', 'Location', 'Category']],
    body: tableData,
    startY: 40,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] }
  });
  
  // Family members legend
  let legendY = doc?.lastAutoTable?.finalY + 15;
  doc?.setFontSize(12);
  doc?.text('Family Members:', 14, legendY);
  
  persons?.forEach((person, index) => {
    const yPos = legendY + 8 + (index * 8);
    doc?.setFillColor(person?.color || '#000000');
    doc?.circle(16, yPos - 1, 2, 'F');
    doc?.setFontSize(10);
    doc?.text(`${person?.name} (${person?.role})`, 22, yPos);
  });
  
  doc?.save('family-calendar.pdf');
};

export const exportDailyTasksToPDF = (tasks, persons, date) => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text('Daily Task Management Export', 14, 20);
  
  // Date info
  doc?.setFontSize(11);
  doc?.text(`Date: ${date?.toLocaleDateString() || new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Prepare table data
  const tableData = tasks?.map(task => {
    const person = persons?.find(p => p?.id === task?.personId);
    const timeStr = `${String(task?.time)?.padStart(2, '0')}:00`;
    return [
      timeStr,
      task?.title || '',
      person?.name || '',
      task?.priority || '',
      task?.completed ? 'Yes' : 'No',
      `${task?.duration || 0} min`
    ];
  }) || [];
  
  // Generate table
  doc?.autoTable({
    head: [['Time', 'Task', 'Person', 'Priority', 'Completed', 'Duration']],
    body: tableData,
    startY: 40,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] }
  });
  
  doc?.save('daily-tasks.pdf');
};

export const exportLongTermTasksToPDF = (tasks, familyMembers) => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(18);
  doc?.text('Long-Term Task Planning Export', 14, 20);
  
  // Date info
  doc?.setFontSize(11);
  doc?.text(`Export Date: ${new Date()?.toLocaleDateString()}`, 14, 30);
  
  // Prepare table data
  const tableData = tasks?.map(task => [
    task?.title || '',
    task?.assignedTo || '',
    task?.dueDate || '',
    task?.priority || '',
    task?.status || '',
    `${task?.progress || 0}%`,
    task?.category || ''
  ]) || [];
  
  // Generate table
  doc?.autoTable({
    head: [['Task', 'Assigned To', 'Due Date', 'Priority', 'Status', 'Progress', 'Category']],
    body: tableData,
    startY: 40,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [59, 130, 246] }
  });
  
  doc?.save('long-term-tasks.pdf');
};

// Family Activity Reports Export Functions
export const exportReportToPDF = (tasks, familyMembers, period, dateRange, stats) => {
  const doc = new jsPDF();
  
  // Title
  doc?.setFontSize(20);
  doc?.text('Family Activity Report', 14, 20);
  
  // Report metadata
  doc?.setFontSize(11);
  doc?.text(`Period: ${dateRange}`, 14, 30);
  doc?.text(`Generated: ${new Date()?.toLocaleDateString()}`, 14, 36);
  
  // Overall statistics
  doc?.setFontSize(14);
  doc?.text('Overall Statistics', 14, 48);
  doc?.setFontSize(10);
  doc?.text(`Total Tasks: ${stats?.total}`, 20, 56);
  doc?.text(`Completed: ${stats?.completed} (${stats?.completionRate}%)`, 20, 62);
  doc?.text(`Pending: ${stats?.pending}`, 20, 68);
  doc?.text(`Overdue: ${stats?.overdue}`, 20, 74);
  
  // Member completion rates
  doc?.setFontSize(14);
  doc?.text('Completion Rates by Member', 14, 88);
  
  const memberData = familyMembers?.map(member => {
    const memberTasks = tasks?.filter(t => t?.personId === member?.id);
    const completed = memberTasks?.filter(t => t?.completed)?.length;
    const total = memberTasks?.length;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return [
      member?.name,
      member?.role,
      `${completed}/${total}`,
      `${rate}%`
    ];
  }) || [];
  
  doc?.autoTable({
    head: [['Member', 'Role', 'Completed/Total', 'Rate']],
    body: memberData,
    startY: 94,
    styles: { fontSize: 9 },
    headStyles: { fillColor: [59, 130, 246] }
  });
  
  // Task details
  const taskData = tasks?.map(task => {
    const member = familyMembers?.find(m => m?.id === task?.personId);
    return [
      task?.date || '',
      member?.name || '',
      task?.title || '',
      task?.category || '',
      task?.priority || '',
      task?.completed ? 'Yes' : 'No'
    ];
  }) || [];
  
  doc?.addPage();
  doc?.setFontSize(14);
  doc?.text('Task Details', 14, 20);
  
  doc?.autoTable({
    head: [['Date', 'Member', 'Task', 'Category', 'Priority', 'Completed']],
    body: taskData,
    startY: 26,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [59, 130, 246] }
  });
  
  doc?.save('family-activity-report.pdf');
};

export const exportReportToCSV = (tasks, familyMembers, dateRange) => {
  const csvData = tasks?.map(task => {
    const member = familyMembers?.find(m => m?.id === task?.personId);
    return {
      Date: task?.date || '',
      Member: member?.name || '',
      Role: member?.role || '',
      Task: task?.title || '',
      Category: task?.category || '',
      Priority: task?.priority || '',
      Duration: `${task?.duration || 0} min`,
      Completed: task?.completed ? 'Yes' : 'No'
    };
  }) || [];
  
  exportToCSV(csvData, 'family-activity-report.csv');
};

export const sendReportByEmail = (emailAddress, tasks, familyMembers, dateRange, stats) => {
  // This would integrate with an email service in production
  // For now, it's a placeholder that simulates email sending
  console.log('Sending report to:', emailAddress);
  console.log('Report data:', { tasks, familyMembers, dateRange, stats });
  return Promise.resolve({ success: true });
};

// Import Functions
export const importFromJSON = (file, callback) => {
  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e?.target?.result);
      callback(data, null);
    } catch (error) {
      callback(null, 'Invalid JSON file format');
    }
  };
  
  reader.onerror = () => {
    callback(null, 'Error reading file');
  };
  
  reader?.readAsText(file);
};

// Export family configuration
export const exportFamilyConfig = (familyMembers, preferences) => {
  const config = {
    version: '1.0',
    exportDate: new Date()?.toISOString(),
    familyMembers,
    preferences
  };
  
  const jsonContent = JSON.stringify(config, null, 2);
  downloadFile(jsonContent, 'family-config.json', 'application/json');
};

// Helper function to trigger download
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
  URL.revokeObjectURL(url);
};

export default {
  exportToCSV,
  exportCalendarToPDF,
  exportDailyTasksToPDF,
  exportLongTermTasksToPDF,
  exportReportToPDF,
  exportReportToCSV,
  sendReportByEmail,
  importFromJSON,
  exportFamilyConfig
};